/**
 * Gate I8.11: Workflow Typeahead Search Component
 * 
 * Provides search-as-you-type functionality for finding animals in the workflow.
 * Shows "where they are" context (track, eval status, location/kennel) in results.
 * Supports jump-to-location in both Grid and Board views.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, X, Eye, MapPin, Heart } from "lucide-react";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";
import { useZoneLabels } from "@/hooks/use-zone-labels";
import { 
  type WorkflowDashboardItem, 
  type Track, 
  type EvalStatus,
  TRACK_LABELS,
  EVAL_STATUS_LABELS 
} from "@/lib/api/workflow-types";

interface WorkflowSearchProps {
  items: WorkflowDashboardItem[];
  viewMode: 'grid' | 'board' | 'list';
  onJumpToAnimal: (animalId: string, track: Track, evalStatus: EvalStatus) => void;
  onOpenDetails: (animalId: string) => void;
}

interface SearchResult extends WorkflowDashboardItem {
  matchType: 'id' | 'name';
}

const DEBOUNCE_MS = 200;

export function WorkflowSearch({ 
  items, 
  viewMode, 
  onJumpToAnimal, 
  onOpenDetails 
}: WorkflowSearchProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: followsData } = useFollowedAnimalIds();
  const { resolveLabel } = useZoneLabels();
  const followedIds = followsData?.animalIds ?? [];

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const results = useMemo<SearchResult[]>(() => {
    if (!debouncedQuery.trim()) return [];
    
    const lowerQuery = debouncedQuery.toLowerCase().trim();
    const matches: SearchResult[] = [];
    
    for (const item of items) {
      const idMatch = item.animalId.toLowerCase().startsWith(lowerQuery);
      const nameMatch = item.name?.toLowerCase().includes(lowerQuery);
      
      if (idMatch) {
        matches.push({ ...item, matchType: 'id' });
      } else if (nameMatch) {
        matches.push({ ...item, matchType: 'name' });
      }
    }
    
    matches.sort((a, b) => {
      if (a.matchType !== b.matchType) {
        return a.matchType === 'id' ? -1 : 1;
      }
      return a.animalId.localeCompare(b.animalId);
    });
    
    return matches.slice(0, 20);
  }, [debouncedQuery, items]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsOpen(true);
  }, []);

  const handleClear = useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
    setIsOpen(false);
    inputRef.current?.focus();
  }, []);

  const handleJump = useCallback((item: SearchResult) => {
    onJumpToAnimal(item.animalId, item.track, item.evalStatus);
    setIsOpen(false);
  }, [onJumpToAnimal]);

  const handleOpenDetails = useCallback((e: React.MouseEvent, item: SearchResult) => {
    e.stopPropagation();
    onOpenDetails(item.animalId);
    setIsOpen(false);
  }, [onOpenDetails]);

  const getLocationLabel = (item: WorkflowDashboardItem) => {
    const viewLabel = viewMode === 'grid' ? 'Grid' : viewMode === 'board' ? 'Board' : 'List';
    return `${viewLabel}: ${TRACK_LABELS[item.track]} · ${EVAL_STATUS_LABELS[item.evalStatus]}`;
  };

  return (
    <div ref={containerRef} className="relative w-full sm:w-64">
      <div className="relative flex items-center">
        <Search className="absolute left-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Find animal..."
          value={query}
          onChange={handleInputChange}
          onFocus={() => query && setIsOpen(true)}
          className="pl-9 pr-8 h-9"
          data-testid="workflow-search-input"
        />
        {query && (
          <button
            type="button"
            className="absolute right-2 flex items-center justify-center h-5 w-5 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            onClick={handleClear}
            data-testid="workflow-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isOpen && debouncedQuery && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 overflow-hidden"
          data-testid="workflow-search-results"
        >
          {results.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No animals found matching "{debouncedQuery}"
            </div>
          ) : (
            <ScrollArea className="max-h-[300px]">
              <div className="py-1">
                {results.map((item) => (
                  <div
                    key={item.animalId}
                    className="flex items-center justify-between px-3 py-2 hover:bg-accent cursor-pointer group"
                    onClick={() => handleJump(item)}
                    data-testid={`workflow-search-result-row-${item.animalId}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                          {item.animalId}
                        </span>
                        <span className="font-medium truncate text-sm">
                          {item.name || "Unknown name"}
                        </span>
                        {followedIds.includes(item.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 shrink-0" data-testid={`heart-followed-${item.animalId}`} />}
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">
                          {item.location}
                          {item.zoneCode && ` · ${resolveLabel(item.zoneCode)}`}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {getLocationLabel(item)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => handleOpenDetails(e, item)}
                        data-testid={`workflow-search-open-details-${item.animalId}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}

export default WorkflowSearch;
